import { Api } from "@/api/Api";
import type { RootState } from "@/reducer";
import { isProfileLocked } from "@/utils/profileLock";
import { buildFallbackLayoutsForTab, buildTransientFallbackLayoutsForTab } from "@/utils/tabLayoutFallback";
import Logger from "@mm/logging";
import type { GenericLayoutCreate, Profile, Tab, TabCreate, TabUpdate } from "@mm/metx-workbench.meteomatics.com";
import { createAsyncThunk } from "@reduxjs/toolkit";

const logger = Logger.fromFilename(__filename);

// De-duplicates concurrent layout repairs for the same tab.
const inFlightLayoutRepairs = new Map<Tab["id"], ReturnType<typeof Api.layout.v2ReplaceTabLayouts>>();

const repairTabLayouts = (tabId: Tab["id"], fallbackLayouts: GenericLayoutCreate[]) => {
  const existing = inFlightLayoutRepairs.get(tabId);
  if (existing) {
    return existing;
  }

  const request = Api.layout.v2ReplaceTabLayouts({ genericLayoutCreate: fallbackLayouts, tabId }).finally(() => {
    inFlightLayoutRepairs.delete(tabId);
  });
  inFlightLayoutRepairs.set(tabId, request);
  return request;
};

export const fetchTab = createAsyncThunk("api/tab", async (payLoad: { tabId: Tab["id"] }, _thunkAPI) => {
  return await Api.tab.v2ReadTab({ tabId: payLoad.tabId }).then((tab) => {
    const fallbackLayouts = buildFallbackLayoutsForTab(tab);
    if (!fallbackLayouts) {
      return tab;
    }

    // Don't persist a repair for read-only profiles; render transient layouts instead.
    const state = _thunkAPI.getState() as RootState | undefined;
    const profile = state?.profile?.profiles?.[tab.id_profile];
    if (isProfileLocked(profile)) {
      return { ...tab, layouts: buildTransientFallbackLayoutsForTab(tab) };
    }

    return repairTabLayouts(tab.id, fallbackLayouts)
      .then((layouts) => ({ ...tab, layouts }))
      .catch((error) => {
        logger.error("Failed to repair missing tab layouts. Falling back to transient layouts.", error);
        return { ...tab, layouts: buildTransientFallbackLayoutsForTab(tab) };
      });
  });
});

export const createTab = createAsyncThunk(
  "api/tab/create",
  async (payLoad: { profileId: Profile["id"]; data: TabCreate }, _thunkAPI) => {
    return await Api.tab.v2CreateTab({ tabCreate: payLoad.data, profileId: payLoad.profileId }).then((tab) => {
      return tab;
    });
  },
);

export const importTab = createAsyncThunk(
  "api/tab/copy",
  async (payLoad: { tabId: Tab["id"]; profileId: Profile["id"] }, _thunkAPI) => {
    return await Api.profile
      .v1TabImport({
        bodyV1TabImport: {
          source_tab_id: payLoad.tabId,
          target_profile_id: payLoad.profileId,
        },
      })
      .then((tab) => {
        return tab;
      });
  },
);

export const removeTab = createAsyncThunk(
  "api/tab/delete",
  (payLoad: { tabId: Tab["id"]; profileId: Profile["id"] }, _thunkAPI) => {
    return Api.tab.v2DeleteTab({ tabId: payLoad.tabId }).then(() => {
      return { tabId: payLoad.tabId, profileId: payLoad.profileId };
    });
  },
);

export const updateTabOrders = createAsyncThunk(
  "api/tabs/updateOrders",
  async (payLoad: { tabs: TabUpdate[] }, _thunkAPI) => {
    try {
      const response = await Api.tab.v2UpdateTabOrders({ tabUpdate: payLoad.tabs });
      return payLoad.tabs; // Return the payload as the result of the action
    } catch (error) {
      console.error("Error updating tab orders:", error); // Log any error that occurs during the API call
      throw error; // Re-throw the error to be handled by the caller
    }
  },
);
